import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configure axios to always send cookies AND support token fallback
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Set up axios interceptor to add token to requests as fallback
  useEffect(() => {
    const interceptor = axios.interceptors.request.use((config) => {
      const storedToken = localStorage.getItem('token');
      if (storedToken && !config.headers.Authorization) {
        config.headers.Authorization = `Bearer ${storedToken}`;
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, []);

  // Check auth status on mount - tries cookie first, then token
  useEffect(() => {
    const initAuth = async () => {
      try {
        // First try with cookies (withCredentials is set)
        const response = await axios.get(`${API_URL}/api/auth/me`);
        setUser(response.data);
      } catch (error) {
        // If cookie fails, try with stored token
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
          try {
            const response = await axios.get(`${API_URL}/api/auth/me`, {
              headers: { Authorization: `Bearer ${storedToken}` }
            });
            setUser(response.data);
            setToken(storedToken);
          } catch (tokenError) {
            localStorage.removeItem('token');
            setToken(null);
            setUser(null);
          }
        } else {
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    if (access_token) {
      localStorage.setItem('token', access_token);
      setToken(access_token);
    }
    setUser(userData);
    return userData;
  }, []);

  const register = useCallback(async (username, email, password, userType = 'viewer') => {
    const response = await axios.post(`${API_URL}/api/auth/register`, { 
      username, 
      email, 
      password,
      user_type: userType
    });
    const { access_token, user: userData } = response.data;
    if (access_token) {
      localStorage.setItem('token', access_token);
      setToken(access_token);
    }
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch (error) {
    }
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  }, []);

  const getAuthHeader = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
    } catch (error) {
    }
  }, []);

  const updateUser = useCallback((newUserData) => {
    setUser(prevUser => ({ ...prevUser, ...newUserData }));
  }, []);

  const contextValue = useMemo(() => ({ 
    user, 
    token,
    loading, 
    login, 
    register, 
    logout, 
    getAuthHeader,
    refreshUser,
    updateUser
  }), [user, token, loading, login, register, logout, getAuthHeader, refreshUser, updateUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
